import React, { useContext, useMemo, forwardRef, useLayoutEffect } from 'react';
import cn from 'classnames';
import { useTypography } from '../hooks/useTypography';
import { createVariablesStyleTag, getVariables } from '../utils/theme';
import light from '../themes/light';
import { Fonts } from './fonts';
const ThemeContext = React.createContext(light);
const THEME_DEFAULTS = {
    dir: 'ltr',
};
export function useTheme() {
    return useContext(ThemeContext);
}
export const ThemeProvider = forwardRef(function ThemeProvider({ theme, isGlobal, children, className, style, nonce, ...rest }, ref) {
    var _a;
    const { className: typographyClassName } = useTypography({
        variant: 'body2',
        group: 'tight',
        thickness: 'regular',
    });
    const finalTheme = useMemo(() => ({
        ...THEME_DEFAULTS,
        ...theme,
        // указываем глобальность в теме, чтобы всякие попапы с порталами
        // не прописывали переменные в пустую, тк они уже есть глобально
        global: isGlobal !== null && isGlobal !== void 0 ? isGlobal : theme.global,
    }), [theme, isGlobal]);
    const variables = useMemo(() => {
        return getVariables(finalTheme);
    }, [finalTheme]);
    const finalStyles = useMemo(() => {
        if (finalTheme.global) {
            return style;
        }
        return { ...variables, ...style };
    }, [variables, finalTheme.global]);
    const direction = (_a = theme.dir) !== null && _a !== void 0 ? _a : 'ltr';
    // нужно до рендеринга компонента
    useLayoutEffect(() => {
        const dataSetName = 'themeGlobal';
        const selector = ':root';
        // если тема уже определена, то не трогаем
        if (finalTheme.global && document.documentElement.dataset[dataSetName] === undefined) {
            document.documentElement.dataset[dataSetName] = 'true';
            finalTheme.global && document.documentElement.setAttribute('dir', direction);
            const styleTag = createVariablesStyleTag(selector, variables, nonce);
            document.head.appendChild(styleTag);
            return () => {
                delete document.documentElement.dataset[dataSetName];
                styleTag.remove();
            };
        }
    }, [finalTheme.global, variables]);
    return (React.createElement("div", { ref: ref, style: finalStyles, dir: direction, className: cn(className, typographyClassName), ...rest },
        React.createElement(ThemeContext.Provider, { value: finalTheme }, children),
        React.createElement(Fonts, null)));
});
